// Pure planners for the Sectors panel's owner edits — Story #6837 Stream 8a-2.
//
// PUT /market/{id}/sectors takes 1–5 entries with exactly one primary. The API
// keeps every code the body does not name, makes every code it does name an
// owner entry, and REPLACES the removed list whenever one is sent. So each plan
// is the FULL sector list after the edit, and `removed` is present only when the
// caller actually knows the list (undefined = leave the stored list untouched).
import type { MarketRole, MarketSectorDetail } from '../../types/market'

export const MAX_SECTORS = 5

export type SectorEntry = { code: string; role: MarketRole }
export type SectorWrite = { sectors: SectorEntry[]; removed?: string[] }
type Current = Pick<MarketSectorDetail, 'code' | 'role'>

const withPrimary = (codes: string[], primary: string): SectorEntry[] =>
  codes.map((code) => ({ code, role: code === primary ? 'primary' : 'secondary' }))

const unique = (codes: string[]) => [...new Set(codes)]

export function primaryOf(detail: Current[]): string | undefined {
  return (detail.find((d) => d.role === 'primary') ?? detail[0])?.code
}

/** A secondary becomes primary; the old primary becomes secondary. */
export function planPromote(detail: Current[], removed: string[] | undefined, code: string): SectorWrite | null {
  if (!detail.some((d) => d.code === code)) return null
  return { sectors: withPrimary(detail.map((d) => d.code), code), removed }
}

/** The primary steps down; the first secondary takes over. Null when there is no secondary. */
export function planDemote(detail: Current[], removed: string[] | undefined): SectorWrite | null {
  const primary = primaryOf(detail)
  const next = detail.find((d) => d.code !== primary)
  return next ? { sectors: withPrimary(detail.map((d) => d.code), next.code), removed } : null
}

/** Drop a sector and remember it as removed. The last sector cannot be removed. */
export function planRemove(detail: Current[], removed: string[] | undefined, code: string): SectorWrite | null {
  const rest = detail.filter((d) => d.code !== code)
  if (rest.length === 0 || rest.length === detail.length) return null
  const primary = primaryOf(detail)
  const nextPrimary = code === primary ? rest[0].code : (primary as string)
  return { sectors: withPrimary(rest.map((d) => d.code), nextPrimary), removed: unique([...(removed ?? []), code]) }
}

/** Add a secondary. Null at the 5-sector limit or when the code is already assigned. */
export function planAdd(detail: Current[], removed: string[] | undefined, code: string): SectorWrite | null {
  if (detail.length >= MAX_SECTORS || detail.some((d) => d.code === code)) return null
  return { sectors: withPrimary([...detail.map((d) => d.code), code], primaryOf(detail) as string), removed }
}

/** Bring a removed code back as a secondary and drop it from the removed list. */
export function planRestore(detail: Current[], removed: string[] | undefined, code: string): SectorWrite | null {
  const plan = planAdd(detail, removed, code)
  return plan ? { sectors: plan.sectors, removed: (removed ?? []).filter((c) => c !== code) } : null
}
